import React, { useState } from "react";

/**
 * UsersSettings
 *
 * Manages admin/staff accounts, permission levels, and access roles.
 * This example includes:
 * - A mock list of existing users
 * - A simple form to add new users
 * - TailwindCSS styling for a modern layout
 */
const UsersSettings = () => {
  // Mock existing users
  const [users, setUsers] = useState([
    { id: 1, name: "Alice Admin", email: "alice@example.com", role: "Admin" },
    { id: 2, name: "Bob Staff", email: "bob@example.com", role: "Staff" },
  ]);

  // Form state for adding new user
  const [newUserName, setNewUserName] = useState("");
  const [newUserEmail, setNewUserEmail] = useState("");
  const [newUserRole, setNewUserRole] = useState("Staff"); // default role

  const handleAddUser = (e) => {
    e.preventDefault();
    if (!newUserName || !newUserEmail) {
      alert("Please fill out both name and email fields.");
      return;
    }
    const nextId = users.length ? users[users.length - 1].id + 1 : 1;
    const newUser = {
      id: nextId,
      name: newUserName,
      email: newUserEmail,
      role: newUserRole,
    };
    setUsers([...users, newUser]);
    setNewUserName("");
    setNewUserEmail("");
    setNewUserRole("Staff");
    alert("User added (simulated)!");
  };

  // (Optional) handleDelete, handleRoleChange, etc., could be implemented here.

  return (
    <div className="max-w-4xl mx-auto p-6 space-y-8">
      {/* Page Title */}
      <h1 className="text-2xl font-bold mb-4 bg-gradient-to-r from-blue-400 to-purple-400 bg-clip-text text-transparent">
        User & Staff Management
      </h1>

      {/* Existing Users List */}
      <section className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-6 shadow-sm transition-colors space-y-4">
        <h2 className="text-xl font-semibold text-gray-800 dark:text-gray-100">
          Existing Users
        </h2>
        {users.length === 0 ? (
          <p className="text-gray-600 dark:text-gray-300">
            No users found. Please add a user below.
          </p>
        ) : (
          <table className="w-full text-left text-gray-700 dark:text-gray-300">
            <thead>
              <tr className="border-b border-gray-200 dark:border-gray-700">
                <th className="py-2 px-2 font-medium">Name</th>
                <th className="py-2 px-2 font-medium">Email</th>
                <th className="py-2 px-2 font-medium">Role</th>
                <th className="py-2 px-2 font-medium">Actions</th>
              </tr>
            </thead>
            <tbody>
              {users.map((user) => (
                <tr
                  key={user.id}
                  className="border-b border-gray-100 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                >
                  <td className="py-2 px-2">{user.name}</td>
                  <td className="py-2 px-2">{user.email}</td>
                  <td className="py-2 px-2">{user.role}</td>
                  <td className="py-2 px-2">
                    <button
                      className="px-3 py-1 text-sm font-medium text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300 transition-colors"
                      // onClick={() => handleEdit(user.id)}
                    >
                      Edit
                    </button>
                    <button
                      className="px-3 py-1 text-sm font-medium text-red-600 hover:text-red-800 dark:text-red-400 dark:hover:text-red-300 transition-colors"
                      // onClick={() => handleDelete(user.id)}
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>

      {/* Add New User Form */}
      <section className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-6 shadow-sm transition-colors space-y-4">
        <h2 className="text-xl font-semibold text-gray-800 dark:text-gray-100">
          Add New User
        </h2>
        <form onSubmit={handleAddUser} className="space-y-4">
          {/* Name */}
          <div className="flex flex-col">
            <label
              htmlFor="newUserName"
              className="font-medium text-gray-700 dark:text-gray-300"
            >
              Name
            </label>
            <input
              id="newUserName"
              type="text"
              className="mt-1 p-2 rounded border border-gray-300 dark:border-gray-600 
                         dark:bg-gray-700 dark:text-gray-100 focus:outline-none 
                         focus:ring-2 focus:ring-blue-500"
              value={newUserName}
              onChange={(e) => setNewUserName(e.target.value)}
              placeholder="e.g. John Manager"
            />
          </div>

          {/* Email */}
          <div className="flex flex-col">
            <label
              htmlFor="newUserEmail"
              className="font-medium text-gray-700 dark:text-gray-300"
            >
              Email
            </label>
            <input
              id="newUserEmail"
              type="email"
              className="mt-1 p-2 rounded border border-gray-300 dark:border-gray-600 
                         dark:bg-gray-700 dark:text-gray-100 focus:outline-none 
                         focus:ring-2 focus:ring-blue-500"
              value={newUserEmail}
              onChange={(e) => setNewUserEmail(e.target.value)}
              placeholder="user@mydancecompany.com"
            />
          </div>

          {/* Role */}
          <div className="flex flex-col">
            <label
              htmlFor="newUserRole"
              className="font-medium text-gray-700 dark:text-gray-300"
            >
              Role
            </label>
            <select
              id="newUserRole"
              className="mt-1 p-2 rounded border border-gray-300 dark:border-gray-600 
                         dark:bg-gray-700 dark:text-gray-100 focus:outline-none 
                         focus:ring-2 focus:ring-blue-500"
              value={newUserRole}
              onChange={(e) => setNewUserRole(e.target.value)}
            >
              <option value="Admin">Admin</option>
              <option value="Staff">Staff</option>
              <option value="Viewer">Viewer</option>
              {/* Add more roles as needed */}
            </select>
          </div>

          <div className="flex justify-end">
            <button
              type="submit"
              className="px-6 py-2 bg-blue-600 hover:bg-blue-700 text-white font-semibold 
                         rounded shadow transition-colors"
            >
              Add User
            </button>
          </div>
        </form>
      </section>
    </div>
  );
};

export default UsersSettings;
